import { useState, useEffect } from "react";
import { 
  Heart, Settings2, Sliders, ClipboardList, ShieldAlert, Activity, 
  Plus, Trash2, Edit3, Save, RefreshCw, Loader2, CheckCircle, 
  AlertTriangle, HelpCircle, Eye, User, FileJson, ArrowRight, Check,
  Stethoscope, ShieldCheck, Mail, ChevronDown, ChevronUp, X
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/firebase";
import { doc, setDoc } from "firebase/firestore";
import { 
  loadThresholds, saveThresholds, 
  loadOmsConfig, saveOmsConfig, 
  loadPrograms, saveProgram, 
  DEFAULT_THRESHOLDS, DEFAULT_OMS_THRESHOLDS, DEFAULT_HYPERGLYCEMIA_PROGRAM 
} from "@/services/clinicalConfigService";
import { calculateHomaIR, calculateFib4 } from "@/contexts/UserProfileContext";
import ProgramStudio from "./ProgramStudio";

const CLINICAL_CATEGORIES = [
  {
    id: "diabetes",
    name: "Metabolismo, Control Glucémico y Diabetes",
    description: "Evaluación de resistencia a la insulina, niveles de glicada y control de glucemia basal.",
    icon: Activity,
    color: "teal",
    bgClass: "bg-teal-50 border-teal-100 text-teal-700",
    accentColor: "#2A9D8F",
    biomarkers: ["glucemia", "hba1c", "insulina", "homair"],
    ruleKeywords: ["glucemia", "hba1c", "insulina", "homair"]
  },
  {
    id: "renal",
    name: "Salud Renal y Filtración Glomerular",
    description: "Monitoreo de tasa de filtración estimada (eGFR) y albuminuria (uACR) para detectar nefropatía temprana.",
    icon: Heart,
    color: "indigo",
    bgClass: "bg-indigo-50 border-indigo-100 text-indigo-700",
    accentColor: "#4F46E5",
    biomarkers: ["egfr", "uacr"],
    ruleKeywords: ["egfr", "uacr"]
  },
  {
    id: "hepatic",
    name: "Salud Hepática y Fibrosis (Esteatosis/NASH)",
    description: "Seguimiento de enzimas hepáticas (AST/ALT) y cálculo automatizado del score no invasivo FIB-4.",
    icon: Stethoscope,
    color: "amber",
    bgClass: "bg-amber-50 border-amber-100 text-amber-700",
    accentColor: "#D97706",
    biomarkers: ["fib4", "ast", "alt", "plaquetas"],
    ruleKeywords: ["fib4", "ast", "alt", "plaquetas"]
  }
];

const BIOMARKERS_METADATA = {
  glucemia: { label: "Glucemia Capilar", unit: "mg/dL", category: "diabetes" },
  hba1c: { label: "Hemoglobina Glicada (HbA1c)", unit: "%", category: "diabetes" },
  insulina: { label: "Insulina Sérica", unit: "uIU/mL", category: "diabetes" },
  homair: { label: "Índice HOMA-IR", unit: "", category: "diabetes" },
  egfr: { label: "Tasa Filtración Glomerular (eGFR)", unit: "mL/min/1.73m²", category: "renal" },
  uacr: { label: "Relación Albúmina/Creatinina (uACR)", unit: "mg/g", category: "renal" },
  fib4: { label: "Score FIB-4", unit: "", category: "hepatic" },
  ast: { label: "Enzima Hepática AST", unit: "U/L", category: "hepatic" },
  alt: { label: "Enzima Hepática ALT", unit: "U/L", category: "hepatic" },
  plaquetas: { label: "Plaquetas Sanguíneas", unit: "10^9/L", category: "hepatic" }
};

// ── FÓRMULAS CLÍNICAS DE PRECISIÓN E HI-OS v3.5 ──
const MARTIN_HOPKINS_MATRIX = [
  [5.6, 5.2, 4.8, 4.3, 3.5], // No-HDL: <100
  [6.2, 5.8, 5.4, 4.9, 4.0], // No-HDL: 100-129
  [6.8, 6.3, 5.9, 5.3, 4.4], // No-HDL: 130-159
  [7.4, 6.9, 6.4, 5.8, 4.8], // No-HDL: 160-189
  [8.0, 7.5, 6.9, 6.2, 5.2], // No-HDL: 190-219
  [8.8, 8.2, 7.5, 6.8, 5.7], // No-HDL: >=220
];

const getMartinHopkinsFactor = (tg, noHdl) => {
  let col = 0;
  if (tg < 100) col = 0;
  else if (tg < 150) col = 1;
  else if (tg < 200) col = 2;
  else if (tg < 400) col = 3;
  else col = 4;

  let row = 0;
  if (noHdl < 100) row = 0;
  else if (noHdl < 130) row = 1;
  else if (noHdl < 160) row = 2;
  else if (noHdl < 190) row = 3;
  else if (noHdl < 220) row = 4;
  else row = 5;

  return MARTIN_HOPKINS_MATRIX[row][col];
};

const calculateMartinHopkinsLDL = (cholTotal, hdl, tg) => {
  const noHdl = Math.max(0, cholTotal - hdl);
  if (noHdl === 0 || tg <= 0) {
    return Math.round(Math.max(0, cholTotal - hdl - (tg / 5.0)) * 10) / 10;
  }
  const x = getMartinHopkinsFactor(tg, noHdl);
  const ldl = cholTotal - hdl - (tg / x);
  return Math.round(Math.max(0, ldl) * 10) / 10;
};

const calculateScore2Risk = (age, sex, smoking, systolic, cholTotal, hdl) => {
  const ageClamped = Math.min(Math.max(age, 40), 89);
  const noHdlMgdl = Math.max(0, cholTotal - hdl);
  const noHdlMmol = noHdlMgdl / 38.67;
  const isFemale = sex.toLowerCase() === "female" || sex.toLowerCase() === "f" || sex.toLowerCase() === "mujer";
  const isSmoking = smoking ? 1.0 : 0.0;

  let pi = 0;
  let s0_10 = 0.940;

  if (ageClamped < 70) {
    if (isFemale) {
      pi = 0.62 * ((ageClamped - 60.0) / 10.0) + 0.48 * isSmoking + 0.32 * ((systolic - 120.0) / 20.0) + 0.26 * ((noHdlMmol - 4.0) / 1.5);
      s0_10 = 0.965;
    } else {
      pi = 0.58 * ((ageClamped - 60.0) / 10.0) + 0.52 * isSmoking + 0.28 * ((systolic - 120.0) / 20.0) + 0.24 * ((noHdlMmol - 4.0) / 1.5);
      s0_10 = 0.940;
    }
  } else {
    if (isFemale) {
      pi = 0.45 * ((ageClamped - 75.0) / 10.0) + 0.35 * isSmoking + 0.28 * ((systolic - 120.0) / 20.0) + 0.20 * ((noHdlMmol - 4.0) / 1.5);
      s0_10 = 0.850;
    } else {
      pi = 0.42 * ((ageClamped - 75.0) / 10.0) + 0.40 * isSmoking + 0.25 * ((systolic - 120.0) / 20.0) + 0.18 * ((noHdlMmol - 4.0) / 1.5);
      s0_10 = 0.810;
    }
  }

  const risk = 1.0 - Math.pow(s0_10, Math.exp(pi));
  return Math.round(Math.max(0, risk * 100) * 100) / 100;
};

const calculateCarryingWalkBurn = (basalBurn, weight, heightCm, distanceKm) => {
  const heightM = heightCm / 100.0;
  const idealWeight = Math.pow(heightM, 2) * 23.0;
  const excessWeight = Math.max(0, weight - idealWeight);
  const correction = excessWeight * 0.75 * distanceKm;
  return Math.round((basalBurn + correction) * 100) / 100;
};

const getEstresRisk = (score) => {
  if (score <= 10) return "LEVEL_I";
  if (score <= 12) return "LEVEL_II_A";
  if (score <= 15) return "LEVEL_II_B";
  return "LEVEL_III";
};

const getAnsiedadRisk = (score) => {
  if (score <= 4) return "LEVEL_I";
  if (score <= 9) return "LEVEL_II_A";
  if (score <= 14) return "LEVEL_II_B";
  return "LEVEL_III";
};

const getApotamientoRisk = (score) => {
  if (score <= 18) return "LEVEL_I";
  if (score <= 22) return "LEVEL_II_A";
  if (score <= 27) return "LEVEL_II_B";
  return "LEVEL_III";
};

const getDespersonalizacionRisk = (score) => {
  if (score <= 5) return "LEVEL_I";
  if (score <= 10) return "LEVEL_II_B";
  return "LEVEL_III";
};

const getRealizacionRisk = (score) => {
  if (score >= 25) return "LEVEL_I";
  if (score >= 17) return "LEVEL_II_A";
  return "LEVEL_III";
};

const getBienestarRisk = (score) => {
  if (score >= 16) return "LEVEL_I";
  if (score >= 11) return "LEVEL_II_A";
  return "LEVEL_II_B";
};

const calculateAggregateEmotionalRiskLevel = (metrics) => {
  const risks = [
    getEstresRisk(metrics.estresLaboralScore || 0),
    getAnsiedadRisk(metrics.ansiedadGad7Score || 0),
    getApotamientoRisk(metrics.burnoutAgotamientoScore || 0),
    getDespersonalizacionRisk(metrics.burnoutDespersonalizacionScore || 0),
    getRealizacionRisk(metrics.burnoutRealizacionScore || 32),
    getBienestarRisk(metrics.bienestarGeneralScore || 20)
  ];
  if (risks.includes("LEVEL_III")) return "LEVEL_III";
  if (risks.includes("LEVEL_II_B")) return "LEVEL_II_B";
  if (risks.includes("LEVEL_II_A")) return "LEVEL_II_A";
  return "LEVEL_I";
};

const getFrenoVsVolanteStrategy = (agotamiento, realizacion) => {
  const needsFreno = agotamiento >= 28;
  const needsVolante = realizacion <= 16;
  if (needsFreno && needsVolante) {
    return {
      strategy: "SEQUENTIAL",
      phase1: "FRENO_BIOLOGICO (Semanas 1-2)",
      phase2: "VOLANTE_COGNITIVO (Semanas 3-6)",
      description: "Ambos activos. Fase 1: Freno biológico. Fase 2: Volante cognitivo."
    };
  } else if (needsFreno) {
    return {
      strategy: "FRENO_BIOLOGICO",
      description: "Freno Biológico prioritario: Estimulación tVNS con Pulsetto, descanso y Breathwork."
    };
  } else if (needsVolante) {
    return {
      strategy: "VOLANTE_COGNITIVO",
      description: "Volante Cognitivo prioritario: TCC avanzada, MBCT de desenganche, BrainHQ."
    };
  } else {
    return {
      strategy: "PREVENTIVO",
      description: "Mantenimiento preventivo activo. Técnica Maestra de Coherencia Vital."
    };
  }
};

const parseCondition = (conditionStr) => {
  if (!conditionStr) return { biomarker: "glucemia", operator: ">=", value: 100 };
  const match = conditionStr.match(/^\s*([a-zA-Z0-9_]+)\s*(>=|<=|>|<|==)\s*([0-9.]+)\s*$/);
  if (match) {
    return {
      biomarker: match[1],
      operator: match[2],
      value: parseFloat(match[3])
    };
  }
  return { biomarker: "glucemia", operator: ">=", value: 100, isCustom: true, raw: conditionStr };
};

const renderFriendlyCondition = (conditionStr) => {
  if (!conditionStr) return "Sin condición";
  const parsed = parseCondition(conditionStr);
  if (parsed.isCustom) {
    return `Condición avanzada: ${conditionStr}`;
  }
  const bioMeta = BIOMARKERS_METADATA[parsed.biomarker];
  if (!bioMeta) return conditionStr;
  
  const opLabel = 
    parsed.operator === ">=" ? "es mayor o igual a" :
    parsed.operator === ">" ? "es mayor a" :
    parsed.operator === "<=" ? "es menor o igual a" :
    parsed.operator === "<" ? "es menor a" : "es igual a";
    
  return (
    <span>
      Si <span className="font-extrabold text-slate-800">{bioMeta.label}</span> {opLabel} <span className="font-black text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-md border border-teal-100">{parsed.value} {bioMeta.unit}</span>
    </span>
  );
};

const getActiveToken = (text) => {
  if (!text) return "";
  const match = text.match(/([a-zA-Z0-9_]+)$/);
  return match ? match[1] : "";
};

export default function AdminClinicalSettings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("general"); // "general" | "oms" | "programs" | "simulator"
  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Estados de datos
  const [thresholds, setThresholds] = useState(null);
  const [omsConfig, setOmsConfig] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("hyperglycemia_diabetes");

  // Estado del programa activo en edición
  const [editingProgram, setEditingProgram] = useState(null);

  // Acordeones colapsables para los módulos clínicos en Program Builder
  const [expandedCategories, setExpandedCategories] = useState({
    diabetes: true,
    renal: false,
    hepatic: false
  });

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  // Estados para el constructor modal guiado de reglas
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [modalRule, setModalRule] = useState({
    id: "",
    condition: "",
    targetLevel: 2,
    description: "",
    isCustom: false,
    biomarker: "glucemia",
    operator: ">=",
    value: "100"
  });
  const [modalRuleIsEditing, setModalRuleIsEditing] = useState(false);
  const [modalRuleCategory, setModalRuleCategory] = useState("diabetes");

  // Estado del Simulador Interactivo
  const [simData, setSimUser] = useState({
    age: 45,
    sex: "female",
    systolic: 142,
    diastolic: 88,
    smoking: true,
    diabetes: false,
    talla: 170,
    peso: 91,
    imc: 31.5,
    glucemia: 265, // Alta
    hba1c: 7.2,
    egfr: 28, // Crítico (<30)
    uacr: 320, // Crítico (>300)
    insulina: 15,
    ast: 35,
    alt: 25,
    plaquetas: 140,
    cholTotal: 220,
    hdl: 50,
    tg: 180,
    distanceWalked: 3.5,
    basalBurn: 150,
    estresLaboralScore: 8,
    ansiedadGad7Score: 3,
    burnoutAgotamientoScore: 12,
    burnoutDespersonalizacionScore: 4,
    burnoutRealizacionScore: 28,
    bienestarGeneralScore: 17
  });

  // Carga de datos unificada
  useEffect(() => {
    async function loadClinicalData() {
      setLoading(true);
      try {
        const [loadedThresh, loadedOms, loadedProgs] = await Promise.all([
          loadThresholds(),
          loadOmsConfig(),
          loadPrograms()
        ]);
        setThresholds(loadedThresh);
        setOmsConfig(loadedOms);
        setPrograms(loadedProgs);

        // Buscar y establecer programa activo en edición
        const prog = loadedProgs.find(p => p.id === selectedProgramId) || loadedProgs[0] || DEFAULT_HYPERGLYCEMIA_PROGRAM;
        setEditingProgram(JSON.parse(JSON.stringify(prog)));
      } catch (error) {
        console.error("Error al cargar configuración clínica:", error);
        setStatus({
          type: "error",
          message: "No se pudieron recuperar todos los datos clínicos de Firestore. Usando defaults locales."
        });
      } finally {
        setLoading(false);
      }
    }
    loadClinicalData();
  }, [selectedProgramId]);

  // Guardar Umbrales Basales
  const handleSaveThresholds = async () => {
    setSaving(true);
    setStatus({ type: null, message: "" });
    try {
      await saveThresholds(thresholds);
      setStatus({
        type: "success",
        message: "¡Umbrales clínicos generales actualizados correctamente en Firestore!"
      });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Error al guardar umbrales en Firestore." });
    } finally {
      setSaving(false);
    }
  };

  // Guardar Configuración OMS
  const handleSaveOms = async () => {
    setSaving(true);
    setStatus({ type: null, message: "" });
    try {
      await saveOmsConfig(omsConfig);
      setStatus({
        type: "success",
        message: "¡Reglas de monitoreo cardiovascular de la OMS guardadas en Firestore!"
      });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Error al guardar configuración OMS en Firestore." });
    } finally {
      setSaving(false);
    }
  };

  // Guardar Programa en Edición
  const handleSaveProgram = async () => {
    setSaving(true);
    setStatus({ type: null, message: "" });
    try {
      await saveProgram(editingProgram.id, editingProgram);
      // Actualizar lista de programas localmente
      setPrograms(prev => prev.map(p => p.id === editingProgram.id ? editingProgram : p));
      setStatus({
        type: "success",
        message: `¡Programa Clínico "${editingProgram.name}" actualizado con éxito en Firestore!`
      });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Error al guardar el programa clínico metabólico." });
    } finally {
      setSaving(false);
    }
  };

  // Guardar Programa desde el nuevo ProgramStudio
  const handleSaveProgramFromStudio = async (updatedProgram) => {
    setSaving(true);
    setStatus({ type: null, message: "" });
    try {
      await saveProgram(updatedProgram.id, updatedProgram);
      setEditingProgram(updatedProgram);
      setPrograms(prev => prev.map(p => p.id === updatedProgram.id ? updatedProgram : p));
      setStatus({
        type: "success",
        message: `¡Protocolo Clínico "${updatedProgram.name}" actualizado y publicado con éxito en Firestore!`
      });
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Error al guardar el programa clínico en Firestore." });
    } finally {
      setSaving(false);
    }
  };

  // Agregar una nueva regla abriendo el constructor modal
  const handleOpenRuleModalForCreate = (catId) => {
    let biomarker = "glucemia";
    let value = 200;
    let operator = ">=";
    let description = "Alerta de niveles de glucemia";

    if (catId === "renal") {
      biomarker = "egfr";
      value = 60;
      operator = "<";
      description = "Alerta de filtración glomerular disminuida (eGFR < 60)";
    } else if (catId === "hepatic") {
      biomarker = "fib4";
      value = 1.30;
      operator = ">=";
      description = "Alerta de riesgo elevado de fibrosis hepática (FIB-4 >= 1.30)";
    } else if (catId === "uncategorized") {
      biomarker = "glucemia";
      value = 200;
      operator = ">=";
      description = "Alerta clínica general";
    }

    setModalRule({
      id: `r_${Date.now()}`,
      condition: `${biomarker} ${operator} ${value}`,
      targetLevel: 2,
      description,
      isCustom: false,
      biomarker,
      operator,
      value: value.toString()
    });
    setModalRuleCategory(catId);
    setModalRuleIsEditing(false);
    setIsRuleModalOpen(true);
  };

  // Abrir el constructor modal para editar una regla existente
  const handleOpenRuleModalForEdit = (rule, catId) => {
    const parsed = parseCondition(rule.condition);
    setModalRule({
      id: rule.id,
      condition: rule.condition,
      targetLevel: rule.targetLevel || 2,
      description: rule.description || "",
      isCustom: !!parsed.isCustom,
      biomarker: parsed.biomarker || "glucemia",
      operator: parsed.operator || ">=",
      value: parsed.value !== undefined ? parsed.value.toString() : "100"
    });
    setModalRuleCategory(catId);
    setModalRuleIsEditing(true);
    setIsRuleModalOpen(true);
  };

  // Guardar la regla desde el modal (Crear o Editar)
  const handleSaveModalRule = () => {
    let condition = modalRule.condition;
    if (!modalRule.isCustom) {
      condition = `${modalRule.biomarker} ${modalRule.operator} ${modalRule.value}`;
    }
    
    const finalRule = {
      id: modalRule.id,
      condition,
      targetLevel: modalRule.targetLevel,
      description: modalRule.description || "Nueva regla"
    };

    let updatedRules = [];
    if (modalRuleIsEditing) {
      updatedRules = editingProgram.rules.map(r => r.id === finalRule.id ? finalRule : r);
    } else {
      updatedRules = [...(editingProgram.rules || []), finalRule];
    }

    setEditingProgram({
      ...editingProgram,
      rules: updatedRules
    });
    
    setIsRuleModalOpen(false);
  };

  // Eliminar una regla del programa en edición
  const handleRemoveProgramRule = (ruleId) => {
    setEditingProgram({
      ...editingProgram,
      rules: (editingProgram.rules || []).filter(r => r.id !== ruleId)
    });
  };

  // Autocompletar sugerencias en el editor avanzado
  const handleSelectSuggestion = (biomarkerKey) => {
    const text = modalRule.condition || "";
    const activeToken = getActiveToken(text);
    let newText = text;
    if (activeToken) {
      newText = text.substring(0, text.length - activeToken.length) + biomarkerKey;
    } else {
      newText = text + biomarkerKey;
    }
    setModalRule(prev => ({
      ...prev,
      condition: newText + " "
    }));
  };

  // Modificar rangos de biomarcadores
  const handleUpdateBiomarkerRange = (biomarkerKey, index, field, val) => {
    const biomarker = editingProgram.biomarkers[biomarkerKey];
    const updatedRanges = [...biomarker.ranges];
    updatedRanges[index] = { ...updatedRanges[index], [field]: val };
    
    setEditingProgram({
      ...editingProgram,
      biomarkers: {
        ...editingProgram.biomarkers,
        [biomarkerKey]: {
          ...biomarker,
          ranges: updatedRanges
        }
      }
    });
  };

  // Agregar rango a un biomarcador
  const handleAddBiomarkerRange = (biomarkerKey) => {
    const biomarker = editingProgram.biomarkers[biomarkerKey];
    const lastRange = biomarker.ranges[biomarker.ranges.length - 1];
    const newRange = {
      min: lastRange ? lastRange.max + 1 : 0,
      max: lastRange ? lastRange.max + 50 : 100,
      color: "yellow",
      label: "Nuevo rango",
      jitai: "Acción de prevención..."
    };
    setEditingProgram({
      ...editingProgram,
      biomarkers: {
        ...editingProgram.biomarkers,
        [biomarkerKey]: {
          ...biomarker,
          ranges: [...biomarker.ranges, newRange]
        }
      }
    });
  };

  // Eliminar rango de un biomarcador
  const handleRemoveBiomarkerRange = (biomarkerKey, index) => {
    const biomarker = editingProgram.biomarkers[biomarkerKey];
    setEditingProgram({
      ...editingProgram,
      biomarkers: {
        ...editingProgram.biomarkers,
        [biomarkerKey]: {
          ...biomarker,
          ranges: biomarker.ranges.filter((_, idx) => idx !== index)
        }
      }
    });
  };

  // RESTAURACIÓN DE VALORES POR DEFECTO
  const handleRestoreDefaults = (type) => {
    if (!window.confirm("¿Estás seguro de restablecer los valores oficiales clínicos? Perderás los cambios no guardados.")) return;
    if (type === "general") {
      setThresholds(JSON.parse(JSON.stringify(DEFAULT_THRESHOLDS)));
    } else if (type === "oms") {
      setOmsConfig(JSON.parse(JSON.stringify(DEFAULT_OMS_THRESHOLDS)));
    } else if (type === "programs") {
      setEditingProgram(JSON.parse(JSON.stringify(DEFAULT_HYPERGLYCEMIA_PROGRAM)));
    }
    setStatus({
      type: "success",
      message: "Se han cargado los valores oficiales de referencia. Presione 'Guardar' para aplicarlos en Firestore."
    });
  };

  // EVALUAR EL SIMULADOR 360 CLÍNICO
  const evaluateSimulator360 = () => {
    if (!thresholds || !omsConfig || !editingProgram) return null;

    const results = {
      generalAlerts: [],
      omsTriggered: false,
      omsMatchingCount: 0,
      programTriageLevel: 0,
      programMatchingRules: [],
      jitaiMessages: [],
      serviceRequest: null,
      homair: calculateHomaIR(simData.glucemia, simData.insulina),
      fib4: calculateFib4(simData.age.toString(), simData.ast, simData.alt, simData.plaquetas),
      // MÉTRICAS EXCLUSIVAS HI-OS v3.5
      ldlMartinHopkins: calculateMartinHopkinsLDL(simData.cholTotal, simData.hdl, simData.tg),
      score2Risk: calculateScore2Risk(simData.age, simData.sex, simData.smoking, simData.systolic, simData.cholTotal, simData.hdl),
      carryingWalkBurn: calculateCarryingWalkBurn(simData.basalBurn, simData.peso, simData.talla, simData.distanceWalked),
      aggregateEmotionalRisk: calculateAggregateEmotionalRiskLevel({
        estresLaboralScore: simData.estresLaboralScore,
        ansiedadGad7Score: simData.ansiedadGad7Score,
        burnoutAgotamientoScore: simData.burnoutAgotamientoScore,
        burnoutDespersonalizacionScore: simData.burnoutDespersonalizacionScore,
        burnoutRealizacionScore: simData.burnoutRealizacionScore,
        bienestarGeneralScore: simData.bienestarGeneralScore
      }),
      frenoVolanteStrategy: getFrenoVsVolanteStrategy(simData.burnoutAgotamientoScore, simData.burnoutRealizacionScore)
    };

    // 1. Evaluar Umbrales Generales
    const t = thresholds;
    const sys = simData.systolic;
    const dia = simData.diastolic;
    const glu = simData.glucemia;

    if (sys >= t.paSistolica.critical || dia >= t.paDiastolica.critical) {
      results.generalAlerts.push({ label: "Crisis Hipertensiva", severity: "critical", msg: "Sugerencia de valoración médica inmediata con Médico General / Guardia." });
    } else if (sys >= t.paSistolica.danger || dia >= t.paDiastolica.danger) {

      results.generalAlerts.push({ label: "Hipertensión Estadio II", severity: "danger", msg: "Alerta de severidad alta. Requiere contacto clínico en 24 horas." });
    }

    if (glu >= t.glucemia.severe) {
      results.generalAlerts.push({ label: "Hiperglucemia Severa", severity: "critical", msg: "Peligro de cetoacidosis. Alerta enviada al IHO de inmediato." });
    } else if (glu >= t.glucemia.danger) {
      results.generalAlerts.push({ label: "Hiperglucemia Establecida", severity: "danger", msg: "Alerta de control. Alerta enviada al Médico de Familia." });
    }

    // 2. Evaluar Score de la OMS
    const o = omsConfig.rules;
    let omsMatches = 0;
    if (o.age.enabled && simData.age >= o.age.value) omsMatches++;
    if (o.systolic.enabled && simData.systolic >= o.systolic.value) omsMatches++;
    if (o.diastolic.enabled && simData.diastolic >= o.diastolic.value) omsMatches++;
    if (o.smoking.enabled && simData.smoking === true) omsMatches++;
    if (o.diabetes.enabled && simData.diabetes === true) omsMatches++;
    if (o.imc.enabled && simData.imc >= o.imc.value) omsMatches++;

    results.omsMatchingCount = omsMatches;
    results.omsTriggered = omsMatches >= omsConfig.triggerLogic.minConditions;

    // 3. Evaluar Programa Clínico Multi-Patología
    const vars = {
      glucemia: simData.glucemia,
      hba1c: simData.hba1c,
      egfr: simData.egfr,
      uacr: simData.uacr,
      insulina: simData.insulina,
      homair: results.homair || 0,
      fib4: results.fib4 || 0,
      ast: simData.ast,
      alt: simData.alt,
      plaquetas: simData.plaquetas,
      // NUEVOS BIOMARCADORES DE PRECISIÓN
      cholTotal: simData.cholTotal,
      hdl: simData.hdl,
      tg: simData.tg,
      ldlMartinHopkins: results.ldlMartinHopkins,
      score2Risk: results.score2Risk,
      carryingWalkBurn: results.carryingWalkBurn,
      estresLaboralScore: simData.estresLaboralScore,
      ansiedadGad7Score: simData.ansiedadGad7Score,
      burnoutAgotamientoScore: simData.burnoutAgotamientoScore,
      burnoutDespersonalizacionScore: simData.burnoutDespersonalizacionScore,
      burnoutRealizacionScore: simData.burnoutRealizacionScore,
      bienestarGeneralScore: simData.bienestarGeneralScore,
      aggregateEmotionalRisk: results.aggregateEmotionalRisk
    };

    // Evaluar reglas del programa clínico para determinar el nivel de triaje (el nivel más alto alcanzado gana)
    let maxLevel = 0;
    editingProgram.rules.forEach(rule => {
      try {
        // Un evaluador ultra seguro de expresiones booleanas sencillas (por ejemplo, "glucemia >= 250" o "egfr < 30")
        const expression = rule.condition;
        // Mapear variables a sus valores reales
        const parts = expression.split(" ");
        const variableName = parts[0];
        const operator = parts[1];
        const valueToCompare = parseFloat(parts[2]);

        const actualValue = vars[variableName];
        if (actualValue !== undefined && actualValue !== "") {
          let matched = false;
          const numValue = parseFloat(actualValue);
          if (operator === ">=") matched = numValue >= valueToCompare;
          else if (operator === ">") matched = numValue > valueToCompare;
          else if (operator === "<=") matched = numValue <= valueToCompare;
          else if (operator === "<") matched = numValue < valueToCompare;
          else if (operator === "==") matched = numValue === valueToCompare;

          if (matched) {
            results.programMatchingRules.push(rule);
            if (rule.targetLevel > maxLevel) {
              maxLevel = rule.targetLevel;
            }
          }
        }
      } catch (e) {
        console.error("Error al evaluar la regla de programa:", rule, e);
      }
    });

    results.programTriageLevel = maxLevel;

    // Obtener sugerencias JITAI correspondientes a los rangos de biomarcadores actuales
    Object.keys(editingProgram.biomarkers).forEach(key => {
      const bio = editingProgram.biomarkers[key];
      const val = vars[key];
      if (val !== undefined && val !== "") {
        const numVal = parseFloat(val);
        const matchedRange = bio.ranges.find(r => numVal >= r.min && numVal <= r.max);
        if (matchedRange) {
          results.jitaiMessages.push({
            biomarker: bio.label,
            rangeLabel: matchedRange.label,
            color: matchedRange.color,
            jitai: matchedRange.jitai
          });
        }
      }
    });

    // 4. Si es Nivel 3, generar el recurso HL7 FHIR ServiceRequest (Referral Order)
    if (maxLevel === 3) {
      // Determinar especialidad sugerida en base a la regla que gatilló
      let specialtySnomed = "394582007"; // Nefrología por defecto
      let specialtyDisplay = "Nefrología";
      
      const triggeredRuleIds = results.programMatchingRules.map(r => r.id);
      if (triggeredRuleIds.includes("r4")) {
        specialtySnomed = "394584008"; // Gastroenterología
        specialtyDisplay = "Gastroenterología";
      } else if (triggeredRuleIds.includes("r1") || triggeredRuleIds.includes("r5")) {
        specialtySnomed = "394583002"; // Endocrinología
        specialtyDisplay = "Endocrinología";
      }

      results.serviceRequest = {
        resourceType: "ServiceRequest",
        id: "referral-" + crypto.randomUUID().slice(0, 8),
        status: "active",
        intent: "order",
        category: [
          {
            coding: [
              {
                system: "http://snomed.info/sct",
                code: "103693007",
                display: "Consulta diagnóstica por derivación médica"
              }
            ]
          }
        ],
        priority: "routine",
        code: {
          coding: [
            {
              system: "http://snomed.info/sct",
              code: specialtySnomed,
              display: `Sugerencia de Consulta a especialidad médica de ${specialtyDisplay}`
            }
          ],
          text: `Interconsulta médica a la especialidad de ${specialtyDisplay}`
        },
        subject: {
          reference: `Patient/${user?.uid || "mock-user-123"}`,
          display: "Usuario en Monitoreo"
        },
        occurrenceDateTime: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
        authoredOn: new Date().toISOString(),
        requester: {
          display: "Módulo Clínico Inteligente WE360"
        },
        reasonCode: [
          {
            text: results.programMatchingRules.map(r => r.description).join(", ")
          }
        ],
        supportingInfo: results.programMatchingRules.map(r => ({
          reference: `Observation/${r.id}`,
          display: `${r.description} (Regla Evaluada: ${r.condition})`
        }))
      };
    }

    return results;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-[#FCFAEE] rounded-3xl min-h-[500px]">
        <div className="text-center space-y-3 animate-fade-in">
          <Loader2 className="animate-spin text-teal-600 mx-auto" size={36} />
          <p className="text-sm font-semibold text-slate-500">Cargando base de conocimiento clínico...</p>
        </div>
      </div>
    );
  }

  const simResults = evaluateSimulator360();

  return (
    <div className="max-w-7xl mx-auto space-y-6" style={{ contentVisibility: "auto" }}>
      {/* HEADER DE LA SUITE */}
      <div 
        className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5 p-6 rounded-3xl border shadow-sm transition-all duration-300 hover:shadow-md"
        style={{ background: "white", borderColor: "#E2E8F0" }}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center border border-teal-100">
            <Heart className="text-teal-600 animate-pulse" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-black text-slate-800 tracking-tight">Suite de Control Clínico</h3>
            <p className="text-xs text-slate-500 max-w-xl leading-relaxed">
              Consolida la parametrización de alertas basales, el motor de triaje cardiovascular de la OMS, y los Programas Corporativos Visuales.
            </p>
          </div>
        </div>

        {/* Botones de acción unificados */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => handleRestoreDefaults(activeTab === "general" ? "general" : activeTab === "oms" ? "oms" : "programs")}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 hover:text-slate-800 transition-all cursor-pointer shadow-sm active:scale-95"
          >
            <RefreshCw size={13} />
            Restablecer Defaults
          </button>
          
          <button
            onClick={activeTab === "general" ? handleSaveThresholds : activeTab === "oms" ? handleSaveOms : handleSaveProgram}
            disabled={saving || activeTab === "simulator"}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-extrabold text-white shadow-sm transition-all hover:opacity-95 active:scale-95 disabled:opacity-50 cursor-pointer"
            style={{ background: "linear-gradient(135deg, #00628F 0%, #2A9D8F 100%)" }}
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin animate-infinite" size={13} />
                Guardando en Firestore...
              </>
            ) : (
              <>
                <Save size={13} />
                {activeTab === "general" ? "Guardar Umbrales" : activeTab === "oms" ? "Guardar Config OMS" : activeTab === "simulator" ? "Modo Simulación" : "Guardar Programa"}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Banners de estado */}
      {status.type === "success" && (
        <div className="flex items-start gap-3 p-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 text-emerald-800 animate-fade-in">
          <CheckCircle className="shrink-0 mt-0.5 text-emerald-600" size={16} />
          <span className="text-xs font-bold">{status.message}</span>
        </div>
      )}
      {status.type === "error" && (
        <div className="flex items-start gap-3 p-4 rounded-2xl border border-red-100 bg-red-50/50 text-red-800 animate-fade-in">
          <AlertTriangle className="shrink-0 mt-0.5 text-red-600" size={16} />
          <span className="text-xs font-bold">{status.message}</span>
        </div>
      )}

      {/* TABS DE LA SUITE */}
      <div className="flex border-b border-slate-200">
        {[
          { id: "general", label: "Umbrales Basales", icon: Settings2 },
          { id: "oms", label: "Monitoreo OMS", icon: ClipboardList },
          { id: "programs", label: "Program Studio", icon: Activity },
          { id: "simulator", label: "Simulador 360°", icon: Sliders }
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}-btn`}
            onClick={() => {
              setActiveTab(tab.id);
              setStatus({ type: null, message: "" });
            }}
            className={`flex items-center gap-2 px-6 py-4 border-b-2 text-sm font-bold transition-all cursor-pointer ${
              activeTab === tab.id ? "border-teal-600 text-teal-600 font-black" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.id === "programs" && (
              <span className="text-[9px] font-bold bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-100 uppercase tracking-widest ml-1 animate-pulse">Nuevo</span>
            )}
          </button>
        ))}
      </div>

      {/* TAB 1: UMBRALES GENERALES */}
      {activeTab === "general" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.keys(thresholds).map((fieldKey) => {
            const ranges = thresholds[fieldKey];
            let label = fieldKey;
            let unit = "";
            let description = "";

            if (fieldKey === "paSistolica") { label = "Presión Sistólica"; unit = "mmHg"; description = "Límite superior de presión arterial durante la contracción cardíaca."; }
            else if (fieldKey === "paDiastolica") { label = "Presión Diastólica"; unit = "mmHg"; description = "Límite de presión en las arterias durante el reposo cardíaco."; }
            else if (fieldKey === "glucemia") { label = "Glucemia"; unit = "mg/dL"; description = "Nivel de glucosa capilar libre circulante en ayunas."; }
            else if (fieldKey === "oximetria") { label = "Saturación SpO2"; unit = "%"; description = "Porcentaje de hemoglobina unida a oxígeno en capilares."; }
            else if (fieldKey === "stressIndex") { label = "Índice de Estrés"; unit = "Baevsky"; description = "Nivel de estrés autonómico cardíaco calculado por el SDK facial (rPPG)."; }
            else if (fieldKey === "imc") { label = "Índice de Masa Corporal (IMC)"; unit = "kg/m²"; description = "Relación del peso del colaborador respecto a su estatura al cuadrado."; }
            else if (fieldKey === "sueno") { label = "Horas de Sueño"; unit = "h"; description = "Duración mínima saludable reportada por el colaborador."; }

            return (
              <div key={fieldKey} className="bg-white border rounded-3xl p-5 space-y-4 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="space-y-1.5">
                  <h4 className="text-sm font-black text-slate-800">{label}</h4>
                  <p className="text-[11px] text-slate-400 leading-relaxed">{description}</p>
                </div>

                <div className="border-t pt-3 space-y-2.5">
                  {Object.keys(ranges).map((rangeKey) => (
                    <div key={rangeKey} className="flex items-center justify-between gap-3 text-xs">
                      <span className="capitalize font-bold text-slate-600 flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${
                          rangeKey === "critical" || rangeKey === "hypoCritical" ? "bg-red-500 animate-pulse" : rangeKey === "danger" || rangeKey === "hypo" ? "bg-orange-500" : "bg-blue-500"
                        }`} />
                        {rangeKey === "hypoCritical" ? "Hipoglucemia Crítica" : rangeKey === "hypo" ? "Hipoglucemia" : rangeKey === "severe" ? "Hiperglucemia Severa" : rangeKey}
                      </span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={ranges[rangeKey]}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setThresholds({
                              ...thresholds,
                              [fieldKey]: {
                                ...ranges,
                                [rangeKey]: val
                              }
                            });
                          }}
                          className="w-16 py-1 text-center bg-slate-50 border rounded-lg text-xs font-black focus:outline-none focus:ring-1 focus:ring-teal-500"
                        />
                        <span className="text-[10px] text-slate-400 font-semibold">{unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* TAB 2: MÓDULO OMS */}
      {activeTab === "oms" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna Izquierda: Reglas */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-xs font-extrabold text-slate-400 tracking-widest uppercase">Variables del score OMS</h4>
            <div className="space-y-3">
              {Object.keys(omsConfig.rules).map((key) => {
                const rule = omsConfig.rules[key];
                const isBooleanType = typeof rule.value === "boolean";
                
                return (
                  <div
                    key={key}
                    className={`p-4 rounded-3xl border transition-all duration-300 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                      rule.enabled ? "border-teal-100 shadow-[0_4px_12px_rgba(42,157,143,0.02)]" : "border-slate-100 opacity-60"
                    }`}
                  >
                    <div className="space-y-1 max-w-md">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded tracking-wide ${
                          rule.enabled ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"
                        }`}>
                          {rule.enabled ? "ACTIVO" : "INACTIVO"}
                        </span>
                        <h4 className="text-xs font-black text-slate-800">{rule.label}</h4>
                      </div>
                      <p className="text-[11px] text-slate-400 leading-normal">{rule.description}</p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {rule.enabled && !isBooleanType && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-slate-400">Umbral (≥):</span>
                          <input
                            type="number"
                            value={rule.value}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setOmsConfig({
                                ...omsConfig,
                                rules: {
                                  ...omsConfig.rules,
                                  [key]: { ...rule, value: val }
                                }
                              });
                            }}
                            className="w-16 text-center py-1 rounded-lg border text-xs font-bold bg-slate-50 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          />
                          <span className="text-[10px] text-slate-500 font-semibold">{rule.unit}</span>
                        </div>
                      )}

                      {/* Toggle */}
                      <button
                        onClick={() => {
                          setOmsConfig({
                            ...omsConfig,
                            rules: {
                              ...omsConfig.rules,
                              [key]: { ...rule, enabled: !rule.enabled }
                            }
                          });
                        }}
                        className={`w-11 h-6 rounded-full p-0.5 transition-colors duration-300 relative cursor-pointer focus:outline-none ${
                          rule.enabled ? "bg-teal-600" : "bg-slate-200"
                        }`}
                      >
                        <div 
                          className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${
                            rule.enabled ? "translate-x-5" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Columna Derecha: Lógica */}
          <div className="space-y-4">
            <h4 className="text-xs font-extrabold text-slate-400 tracking-widest uppercase">Lógica de Coincidencia</h4>
            <div className="bg-white border p-6 rounded-3xl space-y-5 shadow-sm">
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <label className="font-bold text-slate-700">Condiciones simultáneas</label>
                  <span className="text-[10px] font-black bg-teal-50 text-teal-700 px-2.5 py-1 rounded-lg border border-teal-100">
                    Al menos {omsConfig.triggerLogic.minConditions} de {Object.values(omsConfig.rules).filter(r => r.enabled).length}
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max={Object.values(omsConfig.rules).filter(r => r.enabled).length || 1}
                  value={omsConfig.triggerLogic.minConditions}
                  onChange={(e) => setOmsConfig({
                    ...omsConfig,
                    triggerLogic: {
                      ...omsConfig.triggerLogic,
                      minConditions: parseInt(e.target.value)
                    }
                  })}
                  className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">Severidad de la alerta</label>
                <select
                  value={omsConfig.triggerLogic.alertSeverity}
                  onChange={(e) => setOmsConfig({
                    ...omsConfig,
                    triggerLogic: { ...omsConfig.triggerLogic, alertSeverity: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-xl text-xs font-bold bg-slate-50 focus:outline-none"
                >
                  <option value="info">Informativa</option>
                  <option value="warning">Moderada</option>
                  <option value="danger">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">Mensaje para el Médico de Familia</label>
                <textarea
                  value={omsConfig.triggerLogic.doctorMessageTemplate}
                  onChange={(e) => setOmsConfig({
                    ...omsConfig,
                    triggerLogic: { ...omsConfig.triggerLogic, doctorMessageTemplate: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-2xl text-xs font-medium focus:outline-none h-16 resize-none bg-slate-50 leading-relaxed"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">Mensaje/Acción para el Usuario</label>
                <textarea
                  value={omsConfig.triggerLogic.userActionTemplate}
                  onChange={(e) => setOmsConfig({
                    ...omsConfig,
                    triggerLogic: { ...omsConfig.triggerLogic, userActionTemplate: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-2xl text-xs font-medium focus:outline-none h-16 resize-none bg-slate-50 leading-relaxed"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: PROGRAM BUILDER (WE360 CLINICAL PROGRAM STUDIO) */}
      {activeTab === "programs" && editingProgram && (
        <ProgramStudio
          initialProgram={editingProgram}
          onSaveProgram={handleSaveProgramFromStudio}
          saving={saving}
        />
      )}

      {/* TAB 4: SIMULADOR CLÍNICO 360° */}
      {activeTab === "simulator" && simResults && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-7xl mx-auto">
          {/* Columna Izquierda (lg:col-span-5): Entrada de Datos */}
          <div className="lg:col-span-5 bg-white border p-6 rounded-3xl space-y-4 shadow-sm h-fit">
            <h4 className="text-sm font-black text-slate-800 pb-3 border-b flex items-center gap-2">
              <Sliders size={18} className="text-teal-600" />
              Ingreso de Datos Clínicos
            </h4>

            {/* Rejilla de sliders */}
            <div className="space-y-3.5">
              {/* Edad */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Edad</span>
                  <span className="text-teal-700 font-extrabold">{simData.age} años</span>
                </div>
                <input
                  type="range" min="18" max="100" value={simData.age}
                  onChange={(e) => setSimUser({ ...simData, age: parseInt(e.target.value) })}
                  className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                />
              </div>

              {/* Glucemia */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Glucemia Capilar</span>
                  <span className="text-teal-700 font-extrabold">{simData.glucemia} mg/dL</span>
                </div>
                <input
                  type="range" min="50" max="400" value={simData.glucemia}
                  onChange={(e) => setSimUser({ ...simData, glucemia: parseInt(e.target.value) })}
                  className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                />
              </div>

              {/* HbA1c */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Hemoglobina Glicada (HbA1c)</span>
                  <span className="text-teal-700 font-extrabold">{simData.hba1c} %</span>
                </div>
                <input
                  type="range" min="4" max="15" step="0.1" value={simData.hba1c}
                  onChange={(e) => setSimUser({ ...simData, hba1c: parseFloat(e.target.value) })}
                  className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                />
              </div>

              {/* eGFR */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Filtración Glomerular (eGFR)</span>
                  <span className="text-teal-700 font-extrabold">{simData.egfr} mL/min</span>
                </div>
                <input
                  type="range" min="10" max="150" value={simData.egfr}
                  onChange={(e) => setSimUser({ ...simData, egfr: parseInt(e.target.value) })}
                  className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                />
              </div>

              {/* uACR */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Albúmina/Creatinina (uACR)</span>
                  <span className="text-teal-700 font-extrabold">{simData.uacr} mg/g</span>
                </div>
                <input
                  type="range" min="0" max="600" value={simData.uacr}
                  onChange={(e) => setSimUser({ ...simData, uacr: parseInt(e.target.value) })}
                  className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                />
              </div>

              {/* Insulina */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs font-bold text-slate-600">
                  <span>Insulina Sérica</span>
                  <span className="text-teal-700 font-extrabold">{simData.insulina} uIU/mL</span>
                </div>
                <input
                  type="range" min="1" max="50" value={simData.insulina}
                  onChange={(e) => setSimUser({ ...simData, insulina: parseInt(e.target.value) })}
                  className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                />
              </div>

              {/* Presión Arterial */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-slate-500">PAS (Sistólica): {simData.systolic} mmHg</span>
                  <input
                    type="range" min="90" max="200" value={simData.systolic}
                    onChange={(e) => setSimUser({ ...simData, systolic: parseInt(e.target.value) })}
                    className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-slate-500">PAD (Diastólica): {simData.diastolic} mmHg</span>
                  <input
                    type="range" min="50" max="120" value={simData.diastolic}
                    onChange={(e) => setSimUser({ ...simData, diastolic: parseInt(e.target.value) })}
                    className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                  />
                </div>
              </div>

              {/* Enzimas y Plaquetas (para FIB-4) */}
              <div className="grid grid-cols-3 gap-3 pt-1">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-500">AST: {simData.ast} U/L</span>
                  <input
                    type="range" min="5" max="120" value={simData.ast}
                    onChange={(e) => setSimUser({ ...simData, ast: parseInt(e.target.value) })}
                    className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-500">ALT: {simData.alt} U/L</span>
                  <input
                    type="range" min="5" max="120" value={simData.alt}
                    onChange={(e) => setSimUser({ ...simData, alt: parseInt(e.target.value) })}
                    className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-slate-500">Plaq: {simData.plaquetas}</span>
                  <input
                    type="range" min="50" max="450" value={simData.plaquetas}
                    onChange={(e) => setSimUser({ ...simData, plaquetas: parseInt(e.target.value) })}
                    className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                  />
                </div>
              </div>

              {/* Toggles booleanos */}
              <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setSimUser({ ...simData, sex: simData.sex === "female" ? "male" : "female" })}
                  className={`flex items-center justify-between p-2.5 border rounded-xl text-[10px] font-bold transition-all cursor-pointer ${
                    simData.sex === "female" ? "bg-pink-50/50 border-pink-200 text-pink-800" : "bg-blue-50/50 border-blue-200 text-blue-800"
                  }`}
                >
                  <span>Sexo</span>
                  <span className="text-[10px] font-black uppercase">{simData.sex === "female" ? "M" : "V"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSimUser({ ...simData, smoking: !simData.smoking })}
                  className={`flex items-center justify-between p-2.5 border rounded-xl text-[10px] font-bold transition-all cursor-pointer ${
                    simData.smoking ? "bg-teal-50/50 border-teal-200 text-teal-800" : "bg-slate-50 border-slate-200 text-slate-500"
                  }`}
                >
                  <span>¿Fuma?</span>
                  <span className="text-[10px] font-black uppercase">{simData.smoking ? "Sí" : "No"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setSimUser({ ...simData, diabetes: !simData.diabetes })}
                  className={`flex items-center justify-between p-2.5 border rounded-xl text-[10px] font-bold transition-all cursor-pointer ${
                    simData.diabetes ? "bg-teal-50/50 border-teal-200 text-teal-800" : "bg-slate-50 border-slate-200 text-slate-500"
                  }`}
                >
                  <span>¿Diab?</span>
                  <span className="text-[10px] font-black uppercase">{simData.diabetes ? "Sí" : "No"}</span>
                </button>
              </div>

              {/* Perfil Lipídico */}
              <div className="border-t pt-3.5 space-y-2.5">
                <span className="text-xs font-black text-slate-600 block uppercase tracking-wider">Perfil Lipídico Avanzado (cLDL)</span>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs font-bold text-slate-600">
                      <span>Colesterol Total</span>
                      <span className="text-teal-700 font-extrabold">{simData.cholTotal} mg/dL</span>
                    </div>
                    <input
                      type="range" min="100" max="400" value={simData.cholTotal}
                      onChange={(e) => setSimUser({ ...simData, cholTotal: parseInt(e.target.value) })}
                      className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500">HDL: {simData.hdl} mg/dL</span>
                      <input
                        type="range" min="20" max="100" value={simData.hdl}
                        onChange={(e) => setSimUser({ ...simData, hdl: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500">Triglicéridos: {simData.tg} mg/dL</span>
                      <input
                        type="range" min="50" max="500" value={simData.tg}
                        onChange={(e) => setSimUser({ ...simData, tg: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Biomecánica de Traslado */}
              <div className="border-t pt-3.5 space-y-2.5">
                <span className="text-xs font-black text-slate-600 block uppercase tracking-wider">Caminata de Traslado (Biomecánica)</span>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500">Peso: {simData.peso} kg</span>
                      <input
                        type="range" min="40" max="150" value={simData.peso}
                        onChange={(e) => {
                          const w = parseInt(e.target.value);
                          const hM = simData.talla / 100.0;
                          setSimUser({ ...simData, peso: w, imc: Math.round((w / Math.pow(hM, 2)) * 10) / 10 });
                        }}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500">Talla: {simData.talla} cm</span>
                      <input
                        type="range" min="140" max="210" value={simData.talla}
                        onChange={(e) => {
                          const h = parseInt(e.target.value);
                          const hM = h / 100.0;
                          setSimUser({ ...simData, talla: h, imc: Math.round((simData.peso / Math.pow(hM, 2)) * 10) / 10 });
                        }}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500">Distancia: {simData.distanceWalked} km</span>
                      <input
                        type="range" min="0.5" max="15.0" step="0.5" value={simData.distanceWalked}
                        onChange={(e) => setSimUser({ ...simData, distanceWalked: parseFloat(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500">Quema Basal: {simData.basalBurn} kcal</span>
                      <input
                        type="range" min="50" max="500" value={simData.basalBurn}
                        onChange={(e) => setSimUser({ ...simData, basalBurn: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Salud Emocional Multidimensional */}
              <div className="border-t pt-3.5 space-y-2.5">
                <span className="text-xs font-black text-slate-600 block uppercase tracking-wider">Salud Emocional (6 Dimensiones)</span>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 block leading-tight">Estrés Lab (0-20): {simData.estresLaboralScore}</span>
                      <input
                        type="range" min="0" max="20" value={simData.estresLaboralScore}
                        onChange={(e) => setSimUser({ ...simData, estresLaboralScore: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 block leading-tight">Ansiedad GAD-7 (0-21): {simData.ansiedadGad7Score}</span>
                      <input
                        type="range" min="0" max="21" value={simData.ansiedadGad7Score}
                        onChange={(e) => setSimUser({ ...simData, ansiedadGad7Score: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <span className="text-[9px] font-bold text-slate-500 block leading-tight">Agot: {simData.burnoutAgotamientoScore}</span>
                      <input
                        type="range" min="0" max="32" value={simData.burnoutAgotamientoScore}
                        onChange={(e) => setSimUser({ ...simData, burnoutAgotamientoScore: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] font-bold text-slate-500 block leading-tight">Despers: {simData.burnoutDespersonalizacionScore}</span>
                      <input
                        type="range" min="0" max="20" value={simData.burnoutDespersonalizacionScore}
                        onChange={(e) => setSimUser({ ...simData, burnoutDespersonalizacionScore: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[9px] font-bold text-slate-500 block leading-tight">Realiz: {simData.burnoutRealizacionScore}</span>
                      <input
                        type="range" min="0" max="32" value={simData.burnoutRealizacionScore}
                        onChange={(e) => setSimUser({ ...simData, burnoutRealizacionScore: parseInt(e.target.value) })}
                        className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-[10px] font-bold text-slate-500">
                      <span>Bienestar General (WHO-5) (0-20)</span>
                      <span className="text-teal-700 font-extrabold">{simData.bienestarGeneralScore} pts</span>
                    </div>
                    <input
                      type="range" min="0" max="20" value={simData.bienestarGeneralScore}
                      onChange={(e) => setSimUser({ ...simData, bienestarGeneralScore: parseInt(e.target.value) })}
                      className="w-full h-1 bg-slate-100 rounded accent-teal-600"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha (lg:col-span-7): Resultados de Simulación */}
          <div className="lg:col-span-7 space-y-6">
            {/* A. ESTADO DE TRIAJE METABÓLICO */}
            <div className="bg-white border p-6 rounded-3xl space-y-5 shadow-sm">
              <h4 className="text-sm font-black text-slate-800 pb-3 border-b flex items-center gap-2">
                <Activity size={18} className="text-teal-600 animate-pulse" />
                Diagnóstico de Triaje y Care Plan (13 Biomarcadores)
              </h4>

              {/* Panel de Triaje */}
              <div 
                className={`p-6 rounded-2xl border flex items-start gap-4 transition-all duration-300 ${
                  simResults.programTriageLevel === 3 ? "bg-red-50/30 border-red-200 text-red-900" :
                  simResults.programTriageLevel === 2 ? "bg-orange-50/30 border-orange-200 text-orange-900" :
                  simResults.programTriageLevel === 1 ? "bg-yellow-50/30 border-yellow-200 text-yellow-900" :
                  "bg-emerald-50/30 border-emerald-200 text-emerald-900"
                }`}
              >
                <div 
                  className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 shadow-sm ${
                    simResults.programTriageLevel === 3 ? "bg-red-100 text-red-700" :
                    simResults.programTriageLevel === 2 ? "bg-orange-100 text-orange-700" :
                    simResults.programTriageLevel === 1 ? "bg-yellow-100 text-yellow-700" :
                    "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  <Stethoscope size={22} />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-white border">
                      {simResults.programTriageLevel === 0 ? "Nivel 0" : `Nivel ${simResults.programTriageLevel}`}
                    </span>
                    <h4 className="text-sm font-bold tracking-tight">
                      {simResults.programTriageLevel === 3 ? "SUGERENCIA DE ATENCIÓN PRIORITARIA (MÉDICO GENERAL)" :
                       simResults.programTriageLevel === 2 ? "MONITOREO MÉDICO REQUERIDO (MÉDICO GENERAL)" :
                       simResults.programTriageLevel === 1 ? "MONITOREO DE ALERTA (IHO)" :
                       "ESTADO METABÓLICO SALUDABLE"}
                    </h4>
                  </div>
                  <p className="text-xs opacity-80 leading-relaxed pt-0.5">
                    {simResults.programTriageLevel === 3 ? "El usuario requiere valoración médica prioritaria con Médico General para evaluación integral y determinación de interconsulta clínica especializada si aplica (HL7 FHIR ServiceRequest)." :
                     simResults.programTriageLevel === 2 ? "Monitoreo prioritario bajo control del Médico de Familia general para ajuste de plan preventivo y terapéutico." :
                     simResults.programTriageLevel === 1 ? "Acompañamiento por el Oficial de Salud (IHO) y Forochat de hábitos saludables para el seguimiento preventivo temprano." :
                     "El usuario se encuentra en rangos metabólicos basales estables bajo este programa de intervención."}
                  </p>
                </div>
              </div>


              {/* Fórmulas calculadas en caliente */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 border rounded-2xl text-center space-y-0.5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block leading-tight">HOMA-IR (Resist. Insulina)</span>
                  <div className="text-base font-black text-slate-800">{simResults.homair || "—"}</div>
                  <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${
                    simResults.homair >= 3.0 ? "bg-red-50 text-red-700" : simResults.homair >= 2.0 ? "bg-yellow-50 text-yellow-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {simResults.homair >= 3.0 ? "Resistencia Severa" : simResults.homair >= 2.0 ? "Resistencia Moderada" : "Sensibilidad Óptima"}
                  </span>
                </div>

                <div className="p-3 bg-slate-50 border rounded-2xl text-center space-y-0.5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block leading-tight">FIB-4 (Fibrosis Hepática)</span>
                  <div className="text-base font-black text-slate-800">{simResults.fib4 || "—"}</div>
                  <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${
                    simResults.fib4 >= 2.68 ? "bg-red-50 text-red-700" : simResults.fib4 >= 1.30 ? "bg-yellow-50 text-yellow-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {simResults.fib4 >= 2.68 ? "Alto Riesgo" : simResults.fib4 >= 1.30 ? "Riesgo Indeterminado" : "Bajo Riesgo"}
                  </span>
                </div>

                <div className="p-3 bg-slate-50 border rounded-2xl text-center space-y-0.5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block leading-tight">cLDL (Martin-Hopkins)</span>
                  <div className="text-base font-black text-slate-800">{simResults.ldlMartinHopkins || "—"} mg/dL</div>
                  <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${
                    simResults.ldlMartinHopkins >= 130 ? "bg-red-50 text-red-700" : simResults.ldlMartinHopkins >= 100 ? "bg-yellow-50 text-yellow-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {simResults.ldlMartinHopkins >= 130 ? "Elevado" : simResults.ldlMartinHopkins >= 100 ? "Límite" : "Óptimo"}
                  </span>
                </div>

                <div className="p-3 bg-slate-50 border rounded-2xl text-center space-y-0.5">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block leading-tight">Riesgo SCORE2 (ESC 2025)</span>
                  <div className="text-base font-black text-slate-800">{simResults.score2Risk || "—"} %</div>
                  <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${
                    simResults.score2Risk >= 10.0 ? "bg-red-50 text-red-700" : simResults.score2Risk >= 5.0 ? "bg-yellow-50 text-yellow-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {simResults.score2Risk >= 10.0 ? "Riesgo Alto/Muy Alto" : simResults.score2Risk >= 5.0 ? "Riesgo Moderado" : "Riesgo Bajo"}
                  </span>
                </div>

                <div className="p-3 bg-slate-50 border rounded-2xl text-center space-y-1 col-span-2">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block leading-tight">Calorías de Caminata de Traslado Biomecánica</span>
                  <div className="text-lg font-black text-teal-700">{simResults.carryingWalkBurn || "—"} kcal</div>
                  <div className="text-[9px] font-semibold text-slate-500">
                    Gasto basal: {simData.basalBurn} kcal | Corrección por sobrepeso biomecánico (+0.75 kcal/km*kg): +{Math.round((simResults.carryingWalkBurn - simData.basalBurn) * 10) / 10} kcal
                  </div>
                </div>
              </div>

              {/* Reglas que gatillaron */}
              <div className="space-y-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Reglas de Triaje que Coincidieron:</span>
                {simResults.programMatchingRules.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Ninguna regla clínica del programa fue gatillada.</p>
                ) : (
                  <div className="space-y-1.5">
                    {simResults.programMatchingRules.map((rule, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50/50 border rounded-xl text-xs font-semibold text-slate-700">
                        <Check className="text-emerald-500 shrink-0" size={14} />
                        <span>{rule.description}</span>
                        <span className="text-[9px] font-mono font-bold bg-indigo-50 border text-indigo-700 px-1.5 py-0.2 rounded ml-auto">{rule.condition}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* A2. DIAGNÓSTICO DE TRIAJE EMOCIONAL MULTIDIMENSIONAL (WE360 PMX) */}
            <div className="bg-white border p-6 rounded-3xl space-y-5 shadow-sm">
              <h4 className="text-sm font-black text-slate-800 pb-3 border-b flex items-center gap-2">
                <Heart size={18} className="text-pink-600 animate-pulse" />
                Diagnóstico de Salud Emocional y Mental (WE360 PMX)
              </h4>

              {/* Panel de Triaje Emocional */}
              <div 
                className={`p-6 rounded-2xl border flex items-start gap-4 transition-all duration-300 ${
                  simResults.aggregateEmotionalRisk === "LEVEL_III" ? "bg-red-50/30 border-red-200 text-red-900" :
                  simResults.aggregateEmotionalRisk === "LEVEL_II_B" ? "bg-orange-50/30 border-orange-200 text-orange-900" :
                  simResults.aggregateEmotionalRisk === "LEVEL_II_A" ? "bg-yellow-50/30 border-yellow-200 text-yellow-900" :
                  "bg-emerald-50/30 border-emerald-200 text-emerald-900"
                }`}
              >
                <div 
                  className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 shadow-sm ${
                    simResults.aggregateEmotionalRisk === "LEVEL_III" ? "bg-red-100 text-red-700" :
                    simResults.aggregateEmotionalRisk === "LEVEL_II_B" ? "bg-orange-100 text-orange-700" :
                    simResults.aggregateEmotionalRisk === "LEVEL_II_A" ? "bg-yellow-100 text-yellow-700" :
                    "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  <Heart size={22} />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-white border">
                      {simResults.aggregateEmotionalRisk === "LEVEL_I" ? "Nivel I" :
                       simResults.aggregateEmotionalRisk === "LEVEL_II_A" ? "Nivel II-A" :
                       simResults.aggregateEmotionalRisk === "LEVEL_II_B" ? "Nivel II-B" :
                       "Nivel III"}
                    </span>
                    <h4 className="text-sm font-bold tracking-tight">
                      {simResults.aggregateEmotionalRisk === "LEVEL_III" ? "ATENCIÓN CRÍTICA REQUERIDA (MEDICINA/PSICOLOGÍA)" :
                       simResults.aggregateEmotionalRisk === "LEVEL_II_B" ? "RIESGO SUB-CLÍNICO (TERAPEUTA/IHO)" :
                       simResults.aggregateEmotionalRisk === "LEVEL_II_A" ? "RANGO AMORTIGUABLE (COACH DE SALUD)" :
                       "SALUD EMOCIONAL ESTABLE"}
                    </h4>
                  </div>
                  <p className="text-xs opacity-80 leading-relaxed pt-0.5">
                    {simResults.aggregateEmotionalRisk === "LEVEL_III" ? "Riesgo clínico severo en uno o más dominios emocionales. Activación síncrona de Primeros Auxilios Psicológicos (PAP) y derivación silenciosa al Oficial de Salud." :
                     simResults.aggregateEmotionalRisk === "LEVEL_II_B" ? "Riesgo sub-clínico establecido. Se recomienda seguimiento periódico por Oficial de Salud (IHO) y sesiones de desenganche cognitivo." :
                     simResults.aggregateEmotionalRisk === "LEVEL_II_A" ? "Vulnerabilidad leve amortiguable mediante micro-pasos conductuales diarios y forochats de resiliencia cognitiva." :
                     "Niveles emocionales en rangos normales y saludables. Mantenimiento de la Coherencia Vital activo."}
                  </p>
                </div>
              </div>

              {/* Detalle de Estrategia Freno vs Volante */}
              <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-2xl space-y-1.5">
                <span className="text-[10px] font-black text-indigo-800 uppercase tracking-widest block">Estrategia de Intervención: Freno vs. Volante</span>
                <div className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] uppercase font-black">
                    {simResults.frenoVolanteStrategy.strategy}
                  </span>
                  {simResults.frenoVolanteStrategy.phase1 && (
                    <span className="text-slate-600">
                      {simResults.frenoVolanteStrategy.phase1} ➔ {simResults.frenoVolanteStrategy.phase2}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  {simResults.frenoVolanteStrategy.description}
                </p>
              </div>
            </div>

            {/* B. RECOMENDACIONES JITAI DINÁMICAS */}
            <div className="bg-white border p-6 rounded-3xl space-y-4 shadow-sm">
              <h4 className="text-sm font-black text-slate-800 pb-3 border-b flex items-center gap-2">
                <Mail size={18} className="text-teal-600" />
                Sugerencias JITAI Personalizadas (Mensajes en Caliente)
              </h4>

              <div className="space-y-3">
                {simResults.jitaiMessages.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-2xl border flex gap-3 text-xs leading-relaxed ${
                      msg.color === "red" ? "bg-red-50/30 border-red-200 text-red-900" :
                      msg.color === "orange" ? "bg-orange-50/30 border-orange-200 text-orange-900" :
                      msg.color === "yellow" ? "bg-yellow-50/30 border-yellow-200 text-yellow-900" :
                      "bg-emerald-50/30 border-emerald-200 text-emerald-900"
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold uppercase text-[10px]">{msg.biomarker}:</span>
                        <span className="text-[9px] font-black tracking-wide border px-1.5 py-0.2 rounded bg-white">{msg.rangeLabel}</span>
                      </div>
                      <p className="font-medium opacity-90 italic">"{msg.jitai}"</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* C. SUGERENCIA DE CONSULTA INTEROPERABLE FHIR SERVICEREQUEST (SÓLO NIVEL 3) */}
            {simResults.serviceRequest && (
              <div className="bg-slate-900 rounded-3xl p-6 space-y-4 shadow-xl text-slate-100 animate-fade-in border border-slate-800">
                <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                  <div className="flex items-center gap-2">
                    <FileJson className="text-teal-400" size={18} />
                    <h4 className="text-sm font-black tracking-tight text-white">Recurso HL7 FHIR Generado (ServiceRequest)</h4>
                  </div>
                  <span className="text-[9px] font-black bg-teal-500/10 text-teal-400 border border-teal-500/20 px-2 py-0.5 rounded uppercase tracking-widest">Listo para HIS/EHR</span>
                </div>

                <p className="text-xs text-slate-400 leading-normal font-medium">
                  Sugerencia de Consulta clínica con codificación SNOMED-CT e interconsulta estructurada para sincronizar por GCP Healthcare API:
                </p>

                <pre className="text-[10px] font-mono bg-slate-950 p-4 rounded-2xl overflow-x-auto text-teal-300 border border-slate-800 max-h-80 leading-relaxed scrollbar-thin">
                  {JSON.stringify(simResults.serviceRequest, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL DE CREACIÓN / EDICIÓN GUIADA DE REGLAS DE TRIAJE (CONSTRUCTOR VISUAL & ANTIERRORES) */}
      {isRuleModalOpen && modalRule && (
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto transition-all duration-300 animate-fade-in">
          <div className="bg-white/95 border border-slate-200/80 rounded-[32px] w-full max-w-xl shadow-[0_32px_100px_rgba(15,23,42,0.12)] overflow-hidden flex flex-col transform scale-100 transition-transform duration-300">
            
            {/* Header */}
            <div className="px-8 pt-8 pb-4 flex items-center justify-between border-b border-dashed border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-600">
                  <Sliders size={18} />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">
                    {modalRuleIsEditing ? "Editar Regla de Triaje" : "Crear Regla de Triaje"}
                  </h3>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                    {modalRuleCategory === "uncategorized" ? "Regla General o de Coexistencia" : `Patología: ${modalRuleCategory}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsRuleModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all flex items-center justify-center cursor-pointer border border-slate-200/50"
              >
                <X size={15} />
              </button>
            </div>

            {/* Content */}
            <div className="px-8 py-6 overflow-y-auto space-y-6 max-h-[70vh] scrollbar-thin">
              
              {/* Mode Switch (Sencillo vs Avanzado) */}
              <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-2xl">
                <div className="space-y-0.5">
                  <span className="text-xs font-extrabold text-slate-700">Diseñador Guiado</span>
                  <p className="text-[10px] text-slate-400 font-semibold leading-relaxed">Usa selectores interactivos sin redactar código matemático.</p>
                </div>
                <button
                  onClick={() => {
                    setModalRule(prev => {
                      let condition = prev.condition;
                      if (prev.isCustom) {
                        // Si cambia de custom a guiado, intentamos parsear para que no se pierda todo
                        const parsed = parseCondition(prev.condition);
                        return {
                          ...prev,
                          isCustom: false,
                          biomarker: parsed.biomarker || "glucemia",
                          operator: parsed.operator || ">=",
                          value: parsed.value !== undefined ? parsed.value.toString() : "100"
                        };
                      } else {
                        // Si cambia de guiado a custom, generamos la condición actual
                        condition = `${prev.biomarker} ${prev.operator} ${prev.value}`;
                        return {
                          ...prev,
                          isCustom: true,
                          condition
                        };
                      }
                    });
                  }}
                  className={`text-[10px] font-black tracking-wider uppercase px-3 py-1.5 rounded-xl border transition-all cursor-pointer ${
                    !modalRule.isCustom 
                      ? "bg-white text-teal-600 border-teal-100 shadow-sm" 
                      : "bg-teal-50 border-teal-200 text-teal-700"
                  }`}
                >
                  {modalRule.isCustom ? "Cambiar a Guiado" : "Cambiar a Avanzado"}
                </button>
              </div>

              {!modalRule.isCustom ? (
                /* GUIADOR DE FÓRMULA CLÍNICA */
                <div className="space-y-4">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block pb-1 border-b border-dashed">
                    Criterio Clínico Interactivo
                  </span>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
                    {/* Biomarcador (col-span-6) */}
                    <div className="sm:col-span-6 space-y-1.5">
                      <label className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">A. Biomarcador</label>
                      <select
                        value={modalRule.biomarker}
                        onChange={(e) => {
                          const bio = e.target.value;
                          setModalRule(prev => ({ ...prev, biomarker: bio }));
                        }}
                        className="w-full bg-slate-50 border border-slate-200 p-2.5 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-teal-500"
                      >
                        {Object.entries(BIOMARKERS_METADATA)
                          // Si no es "uncategorized", filtramos por la categoría
                          .filter(([_, meta]) => modalRuleCategory === "uncategorized" || meta.category === modalRuleCategory)
                          .map(([key, meta]) => (
                            <option key={key} value={key}>{meta.label}</option>
                          ))
                        }
                      </select>
                    </div>

                    {/* Operador (col-span-3) */}
                    <div className="sm:col-span-3 space-y-1.5">
                      <label className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">B. Operador</label>
                      <select
                        value={modalRule.operator}
                        onChange={(e) => {
                          const op = e.target.value;
                          setModalRule(prev => ({ ...prev, operator: op }));
                        }}
                        className="w-full bg-slate-50 border border-slate-200 p-2.5 rounded-xl text-xs font-black text-slate-700 focus:outline-none focus:border-teal-500 text-center"
                      >
                        <option value=">=">&gt;= (Mayor o igual)</option>
                        <option value=">">&gt; (Mayor que)</option>
                        <option value="<=">&lt;= (Menor o igual)</option>
                        <option value="<">&lt; (Menor que)</option>
                        <option value="==">== (Igual a)</option>
                      </select>
                    </div>

                    {/* Valor + Unidad (col-span-3) */}
                    <div className="sm:col-span-3 space-y-1.5">
                      <label className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">C. Valor Límite</label>
                      <div className="relative flex items-center">
                        <input
                          type="number"
                          step="any"
                          value={modalRule.value}
                          onChange={(e) => {
                            const val = e.target.value;
                            setModalRule(prev => ({ ...prev, value: val }));
                          }}
                          className="w-full bg-slate-50 border border-slate-200 p-2.5 pr-14 rounded-xl text-xs font-black text-slate-800 focus:outline-none focus:border-teal-500"
                          placeholder="0"
                        />
                        <span className="absolute right-3 text-[10px] font-black text-slate-400 select-none">
                          {BIOMARKERS_METADATA[modalRule.biomarker]?.unit || "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Previsualización Amigable de la Regla en Construcción */}
                  <div className="p-4 bg-teal-50/40 border border-teal-100 rounded-2xl space-y-2">
                    <span className="text-[9px] font-black text-teal-600 uppercase tracking-widest block">Lectura Clínica Humana</span>
                    <p className="text-xs font-medium text-slate-700 leading-relaxed">
                      {renderFriendlyCondition(`${modalRule.biomarker} ${modalRule.operator} ${modalRule.value}`)}
                    </p>
                  </div>
                </div>
              ) : (
                /* CONSTRUCTOR DE FÓRMULA AVANZADA (INPUT MANUAL) */
                <div className="space-y-4">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block pb-1 border-b border-dashed">
                    Fórmula Clínica Manual
                  </span>
                  <div className="space-y-1.5 relative">
                    <label className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Sintaxis de Condición</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={modalRule.condition}
                        onChange={(e) => {
                          const cond = e.target.value;
                          setModalRule(prev => ({ ...prev, condition: cond }));
                        }}
                        className="w-full bg-slate-900 border border-slate-800 p-3 rounded-xl text-xs font-mono font-bold text-teal-400 focus:outline-none focus:border-teal-500 shadow-inner"
                        placeholder="ej: glucemia >= 200 || age > 65"
                      />

                      {/* Dropdown de Autocompletado Inteligente de Biomarcadores */}
                      {(() => {
                        const activeToken = getActiveToken(modalRule.condition || "");
                        const suggestions = Object.entries(BIOMARKERS_METADATA)
                          .filter(([key, meta]) => {
                            const matchesCategory = modalRuleCategory === "uncategorized" || meta.category === modalRuleCategory;
                            if (!matchesCategory) return false;
                            if (!activeToken) return false;
                            return key.toLowerCase().includes(activeToken.toLowerCase()) && key.toLowerCase() !== activeToken.toLowerCase();
                          });

                        if (suggestions.length === 0) return null;

                        return (
                          <div className="absolute left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200/80 rounded-2xl shadow-[0_12px_30px_rgba(15,23,42,0.08)] divide-y divide-slate-50 animate-fade-in">
                            <div className="px-3 py-1.5 bg-slate-50/80 text-[9px] font-black text-slate-400 uppercase tracking-widest border-b select-none">
                              Sugerencias de Biomarcadores ({modalRuleCategory === "uncategorized" ? "Todos" : modalRuleCategory})
                            </div>
                            {suggestions.map(([key, meta]) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => handleSelectSuggestion(key)}
                                className="w-full text-left px-4 py-2.5 hover:bg-teal-50/50 flex items-center justify-between transition-colors focus:outline-none cursor-pointer group"
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${
                                    meta.category === "diabetes" ? "bg-teal-400" :
                                    meta.category === "renal" ? "bg-indigo-400" : "bg-amber-400"
                                  }`} />
                                  <span className="text-xs font-mono font-black text-teal-600 group-hover:text-teal-700">{key}</span>
                                  <span className="text-[11px] font-semibold text-slate-500">— {meta.label}</span>
                                </div>
                                <span className="text-[9px] font-black text-slate-400 bg-slate-100 group-hover:bg-teal-100/50 group-hover:text-teal-600 px-2 py-0.5 rounded transition-all">
                                  {meta.unit ? `${meta.unit}` : "s/u"}
                                </span>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 block leading-relaxed pl-1">
                      Usa nombres de variables planos en minúsculas. Ejemplo: <code className="bg-slate-100 border text-slate-600 px-1 py-0.2 rounded font-mono text-[9px]">glucemia &gt;= 200</code>.
                    </span>
                  </div>
                </div>
              )}

              {/* Triaje y Nivel */}
              <div className="space-y-4 pt-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block pb-1 border-b border-dashed">
                  Nivel de Triaje & Roles Clínicos
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Nivel de Triaje */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Nivel Asignado</label>
                    <select
                      value={modalRule.targetLevel}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setModalRule(prev => ({ ...prev, targetLevel: val }));
                      }}
                      className="w-full bg-slate-50 border border-slate-200 p-2.5 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-teal-500"
                    >
                      <option value={1}>Nivel 1 — Triaje Inicial (IHO / Oficial de Salud)</option>
                      <option value={2}>Nivel 2 — Médico General (Atención Primaria)</option>
                      <option value={3}>Nivel 3 — Sugerencia Prioritaria a Médico General (Recurso FHIR ServiceRequest)</option>
                    </select>
                  </div>

                  {/* Badge Contextual de Nivel */}
                  <div className="flex items-center justify-center rounded-2xl p-4 border text-center h-fit sm:mt-5 bg-slate-50/50">
                    {modalRule.targetLevel === 3 ? (
                      <div className="space-y-1">
                        <span className="text-[9px] font-black bg-rose-50 border border-rose-200 text-rose-700 px-2 py-0.5 rounded-md uppercase tracking-wider">Médico General / Urgente (Nivel 3)</span>
                        <p className="text-[10px] font-bold text-slate-400 leading-normal">Sugerencia prioritaria e interconsulta estructurada interoperable.</p>
                      </div>
                    ) : modalRule.targetLevel === 2 ? (
                      <div className="space-y-1">
                        <span className="text-[9px] font-black bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-md uppercase tracking-wider">Médico General (Nivel 2)</span>
                        <p className="text-[10px] font-bold text-slate-400 leading-normal">Sugerencia de consulta médica general y agendamiento preventivo.</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <span className="text-[9px] font-black bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-md uppercase tracking-wider">Oficial de Salud (Nivel 1)</span>
                        <p className="text-[10px] font-bold text-slate-400 leading-normal">Acompañamiento, telemetría y triaje preventivo inicial.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Acción / Descripción Clínica */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Acción o Mensaje Clínico</label>
                <textarea
                  value={modalRule.description}
                  onChange={(e) => {
                    const desc = e.target.value;
                    setModalRule(prev => ({ ...prev, description: desc }));
                  }}
                  rows="3"
                  className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:border-teal-500 leading-relaxed placeholder-slate-300"
                  placeholder="Ej: Sugerencia de consulta prioritaria con Médico General para evaluación integral de sospecha de Diabetes Mellitus tipo 2."
                />
              </div>


            </div>

            {/* Footer */}
            <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
              <div className="flex flex-col">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sintaxis de Base de Datos</span>
                <code className="text-[10px] font-mono font-bold text-indigo-700 bg-indigo-50/50 border border-indigo-100 px-1.5 py-0.5 rounded-md mt-0.5">
                  {!modalRule.isCustom 
                    ? `${modalRule.biomarker} ${modalRule.operator} ${modalRule.value}` 
                    : modalRule.condition
                  }
                </code>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsRuleModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-black text-slate-500 hover:text-slate-700 bg-white hover:bg-slate-100/80 transition-all border border-slate-200 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveModalRule}
                  className="px-5 py-2 rounded-xl text-xs font-black text-white bg-teal-600 hover:bg-teal-700 transition-all shadow-[0_4px_12px_rgba(13,148,136,0.2)] cursor-pointer flex items-center gap-1.5"
                >
                  <Check size={14} />
                  Guardar Regla
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
